import {
  createFileRoute,
  Navigate,
  useNavigate,
} from '@tanstack/react-router'

const WEB_SIGNUP_URL = `${import.meta.env.VITE_WEB_URL}/?productId=pronteer_annual_web`
import { Preferences } from '@capacitor/preferences'
import { useAuth } from '@/hooks/useAuth'
import { useIsDesktop } from '@/components/ui/use-mobile'
import {
  sendSignInCode,
  signInDemo,
  checkEmailAccount,
  isDemoAccount,
  verifyEmailOtp,
} from '@/lib/auth'
import { setActiveCompanyId } from '@/lib/company-context'
import { applyPendingCompany, PENDING_COMPANY_KEY } from '@/lib/deep-links'
import pronteerLogoFull from '@/assets/pronteer-logo-full.svg'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Loading } from '@/components/ui/loading'
import { useKeyboardHeight } from '@/lib/keyboard'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { track, EVENTS } from '@/utils/analytics'

type SignInSearch = {
  redirect?: string
  email?: string
}

export const Route = createFileRoute('/signin')({
  component: SignInRoute,
  validateSearch: (search: Record<string, unknown>): SignInSearch => {
    return {
      redirect: search.redirect as string | undefined,
      email: search.email as string | undefined,
    }
  },
})

function SignInRoute() {
  const { isLoaded, isSignedIn } = useAuth()
  const { redirect, email: emailParam } = Route.useSearch()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  // Keyboard.resize is "none": shrink the page by the keyboard height so the
  // form and footer visibly compress upward while typing, like other screens.
  const keyboardHeight = useKeyboardHeight()

  const [email, setEmail] = useState(emailParam ?? '')
  const [codeSent, setCodeSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null)
  const [showCompanySelect, setShowCompanySelect] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifyStatus, setVerifyStatus] = useState<
    'idle' | 'matched' | 'failed'
  >('idle')

  // Redirect if already authenticated — also fires after the deep-link
  // handler exchanges the magic-link code and onAuthStateChange flips useAuth.
  if (isLoaded && isSignedIn) {
    // Desktop is the operations-only console — no Home route.
    return <Navigate to={redirect || (isDesktop ? '/operations' : '/home')} />
  }

  const persistPendingCompany = async (companyId: string) => {
    await Preferences.set({ key: PENDING_COMPANY_KEY, value: companyId })
  }

  const handleEmailContinue = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address')
      return
    }

    setLoading(true)
    try {
      // Demo account — bypass magic link, sign in directly with the seeded password
      if (isDemoAccount(email)) {
        await signInDemo(email)
        track(EVENTS.USER_SIGNED_IN, { email, demo: true })
        toast.success('Signed in successfully!')
        setTimeout(() => navigate({ to: redirect || '/home' }), 100)
        return
      }

      const result = await checkEmailAccount(email)
      if (!result.exists || result.companies.length === 0) {
        toast.error('Account not found. Please contact your administrator to be added.')
        return
      }

      if (result.companies.length === 1) {
        const companyId = result.companies[0].id
        setSelectedCompanyId(companyId)
        setCompanies(result.companies)
        await persistPendingCompany(companyId)
        await sendSignInCode(email)
        setCodeSent(true)
        track(EVENTS.USER_SIGNED_IN, { email, stage: 'code-sent' })
      } else {
        setCompanies(result.companies)
        setSelectedCompanyId(result.companies[0].id)
        setShowCompanySelect(true)
      }
    } catch (error: any) {
      console.error('Error sending sign-in code:', error)
      toast.error(error.message || 'Failed to send sign-in code')
    } finally {
      setLoading(false)
    }
  }

  const handleCompanySelected = async () => {
    if (!selectedCompanyId) return
    setLoading(true)
    try {
      await persistPendingCompany(selectedCompanyId)
      await sendSignInCode(email)
      setShowCompanySelect(false)
      setCodeSent(true)
      track(EVENTS.USER_SIGNED_IN, { email, stage: 'code-sent' })
    } catch (error: any) {
      console.error('Error sending sign-in code:', error)
      toast.error(error.message || 'Failed to send sign-in code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (code: string) => {
    if (code.length !== 6 || verifying) return
    setVerifying(true)
    try {
      await verifyEmailOtp(email, code)
      setVerifyStatus('matched')
      await applyPendingCompany()
      track(EVENTS.USER_SIGNED_IN, { email, stage: 'otp-verified' })
      // The isSignedIn redirect at the top of the component handles navigation.
    } catch (error: any) {
      console.error('Error verifying code:', error)
      setVerifyStatus('failed')
      setOtpCode('')
    } finally {
      setVerifying(false)
    }
  }

  const handleResendCode = async () => {
    setLoading(true)
    try {
      await sendSignInCode(email)
      // Resending mints a new token — any previously emailed code is now invalid.
      setOtpCode('')
      setVerifyStatus('idle')
      toast.success('Sign-in code sent again')
    } catch (error: any) {
      console.error('Error resending sign-in code:', error)
      toast.error(error.message || 'Failed to resend sign-in code')
    } finally {
      setLoading(false)
    }
  }

  const handleUseDifferentEmail = async () => {
    setCodeSent(false)
    setShowCompanySelect(false)
    setCompanies([])
    setSelectedCompanyId(null)
    setOtpCode('')
    setVerifyStatus('idle')
    await setActiveCompanyId(null)
    await Preferences.remove({ key: PENDING_COMPANY_KEY })
  }

  if (!isLoaded) {
    return (
      <div className="flex-1 bg-surface-L0 flex items-center justify-center">
        <Loading size={48} />
      </div>
    )
  }

  const isStep2 = codeSent || showCompanySelect

  const heading = codeSent
    ? 'Check your email'
    : showCompanySelect
      ? 'Select your company'
      : 'Sign in'

  return (
    <div
      className={cn(
        'flex-1 bg-surface-L0 flex flex-col overflow-y-auto px-4 pt-[calc(var(--safe-top)+56px)] pb-[calc(var(--safe-bottom)+16px)] transition-[margin-bottom] duration-300 ease-out',
        // Desktop: group logo, form and EULA footer in the vertical center
        // instead of pinning them to the viewport edges.
        isDesktop ? 'justify-center gap-12' : 'justify-between',
      )}
      style={keyboardHeight > 0 ? { marginBottom: keyboardHeight } : undefined}
    >
      {/* Logo */}
      <img
        src={pronteerLogoFull}
        alt="Pronteer"
        className="h-[46px] w-[143px] mx-auto"
      />

      {/* Body */}
      <div className="w-full max-w-[361px] mx-auto flex flex-col gap-8">
        {/* Progress bar */}
        <div className="flex gap-2 w-full">
          <div className="flex-1 h-1 rounded-full bg-background-brand-bold" />
          <div
            className={cn(
              'flex-1 h-1 rounded-full',
              isStep2 ? 'bg-background-brand-bold' : 'bg-surface-L2',
            )}
          />
        </div>

        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-[24px] font-semibold text-content-brand-bold leading-none">
            {heading}
          </h1>
          <p className="text-sm text-content-primary-bold leading-5">
            {codeSent ? (
              <>
                We sent a 6-digit code to{' '}
                <span className="font-bold">{email}</span>. Check your email to
                continue.
              </>
            ) : showCompanySelect ? (
              'Choose the company you want to sign into.'
            ) : (
              'Enter your email address to receive a sign in code.'
            )}
          </p>
        </div>

        {/* Step content + CTA */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!codeSent && !showCompanySelect) handleEmailContinue()
          }}
          className="flex flex-col gap-4"
        >
          {/* Step 1 — email entry */}
          {!codeSent && !showCompanySelect && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-content-primary-bold leading-5">
                  Email address
                </label>
                <Input
                  type="email"
                  inputSize="regular"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email}
                className="w-full h-[45px] rounded-lg bg-background-brand-bold hover:bg-background-brand-bold/90 active:bg-background-brand-bold/80 text-content-primary-inverse text-sm font-semibold"
              >
                {loading ? 'Checking…' : 'Continue'}
              </Button>
              <p className="text-xs text-content-primary-subtle text-center">
                Don&apos;t have an account?{' '}
                <a
                  href={WEB_SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-content-brand-bold font-medium underline"
                >
                  Sign up
                </a>
              </p>
            </>
          )}

          {/* Step 2 — company select */}
          {showCompanySelect && !codeSent && (
            <>
              <div className="flex flex-col gap-2">
                {companies.map((company) => {
                  const selected = selectedCompanyId === company.id
                  return (
                    <button
                      key={company.id}
                      type="button"
                      onClick={() => setSelectedCompanyId(company.id)}
                      className={cn(
                        'w-full p-4 rounded-lg border text-left transition-colors',
                        selected
                          ? 'border-border-brand-bold bg-background-brand-subtle'
                          : 'border-border-L2 bg-surface-L0',
                      )}
                    >
                      <span
                        className={cn(
                          'text-sm font-medium',
                          selected
                            ? 'text-content-brand-bold'
                            : 'text-content-primary-bold',
                        )}
                      >
                        {company.name}
                      </span>
                    </button>
                  )
                })}
              </div>
              <Button
                type="button"
                onClick={handleCompanySelected}
                disabled={loading || !selectedCompanyId}
                className="w-full h-[45px] rounded-lg bg-background-brand-bold hover:bg-background-brand-bold/90 active:bg-background-brand-bold/80 text-content-primary-inverse text-sm font-semibold"
              >
                {loading ? 'Sending…' : 'Send Sign-in Code'}
              </Button>
              <button
                type="button"
                onClick={handleUseDifferentEmail}
                disabled={loading}
                className="text-xs text-content-primary-subtle text-center underline"
              >
                Use a Different Email
              </button>
            </>
          )}

          {/* Step 3 — code sent */}
          {codeSent && (
            <>
              <div className="w-full bg-surface-brand-L1 rounded-2xl p-6 flex flex-col gap-4 items-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => {
                    setOtpCode(value)
                    // Typing a fresh code dismisses the failure message.
                    if (verifyStatus === 'failed') setVerifyStatus('idle')
                  }}
                  disabled={verifying}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  containerClassName="w-full"
                >
                  <InputOTPGroup className="w-full justify-between">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="size-12 border-border-L3"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <Button
                  type="button"
                  variant="brand"
                  onClick={() => handleVerifyCode(otpCode)}
                  disabled={otpCode.length !== 6}
                  loading={verifying}
                  className="w-full"
                >
                  Sign In
                </Button>
                {verifyStatus === 'matched' && (
                  <p className="text-xs font-semibold leading-4 text-content-positive-default text-center">
                    Code matched successfully
                  </p>
                )}
                {verifyStatus === 'failed' && (
                  <p className="text-xs font-semibold leading-4 text-content-negative-bold text-center">
                    Code didn&apos;t match, try again.
                  </p>
                )}
              </div>

              <p className="text-xs font-semibold leading-4 text-content-primary-subtle text-center">
                Didn&apos;t receive the code? Check your spam folder or{' '}
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-content-brand-bold font-semibold"
                >
                  request a new one
                </button>
                .
              </p>
            </>
          )}
        </form>
      </div>

      {/* Bottom footer — EULA before the code is sent, escape hatch after */}
      {codeSent ? (
        <button
          type="button"
          onClick={handleUseDifferentEmail}
          disabled={loading}
          className="text-xs font-semibold leading-4 text-content-primary-subtle text-center underline mx-auto"
        >
          Use a different email
        </button>
      ) : (
        <p className="text-[10px] leading-[1.5] text-content-primary-subtle text-center tracking-[0.15px] mx-auto max-w-[361px]">
          By continuing you confirm that you&apos;ve read our Privacy Policy and
          agree to the{' '}
          <a
            href="https://pronteer.com/eula"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Pronteer End User Licence Agreement
          </a>
          .
        </p>
      )}
    </div>
  )
}
